/**
 * Customer-facing public proposal viewer (Phase 2 + Phase 2L-A25 option groups)
 *
 * Route: /proposals/:token  — no auth, token is the unguessable handle.
 *
 * Behaviour:
 *   • Loads the proposal via GET /api/proposals/public/{token}.
 *   • Renders three line buckets:
 *       — Required (locked / always included)
 *       — Option groups (radio-style: exactly one model per group selected)
 *       — Recommended add-ons (pre-selected, can untick)
 *       — Optional items (default off, customer ticks)
 *   • Highest-priced option per group is the default; "Recommended" /
 *     "Best protection" badge on the default option is admin-controlled
 *     (group.recommended_badge_text).
 *   • Total updates live as the customer toggles selections. Backend
 *     re-computes the final total on approve — the browser cannot
 *     influence pricing.
 *   • Approval payload sends one selected:true per group plus selected:false
 *     for the unchosen siblings (so the snapshot records the offered options).
 *   • An option group always has exactly one member selected, so the
 *     "cannot approve with no selection in a required group" rule is
 *     enforced by construction in the UI.
 *
 * Legacy proposals (no option_groups, no option_group_id on any line)
 * render identically to the pre-A25 layout.
 *
 * No PortalShell on purpose — this is a public page for end customers
 * and should not surface admin nav or auth UI.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Check, ImageIcon, Lock, X, Sparkles, AlertCircle, Star, ExternalLink } from "lucide-react";
import { formatDateTimeAU } from "@/lib/formatDateAU";
import { resolveImageUrl } from "@/lib/imageUrl";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const apiPath = (path) => `${BACKEND}${path}`;

const money = (cents) => {
  const amount = (cents || 0) / 100;
  // AU-style currency with thousands separator, e.g. $5,000.00
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    currencyDisplay: "narrowSymbol",
  }).format(amount);
};

const STATUS_TAG = {
  required:    { label: "Required",    bg: "bg-slate-900",   text: "text-white" },
  recommended: { label: "Recommended", bg: "bg-cyan-600",    text: "text-white" },
  optional:    { label: "Optional",    bg: "bg-slate-200",   text: "text-slate-700" },
};

/**
 * Phase 2L-A28 (Estimate Presentation Polish):
 * Resolve the customer-facing compact description for a hydrated line.
 * Priority:
 *   1. `short_description` (curated, intentionally compact)
 *   2. trimmed `description` (legacy fallback) — first ~140 chars + ellipsis
 *   3. null → render nothing
 * Returns a string or null. Never empty string.
 */
function compactDescription(line) {
  const short = (line.short_description || "").trim();
  if (short) return short;
  const legacy = (line.description || "").trim();
  if (!legacy) return null;
  if (legacy.length <= 140) return legacy;
  return legacy.slice(0, 137).trimEnd() + "…";
}

/**
 * Phase 2L-A28/A29: small "View details" link rendered ONLY when the
 * hydrated line carries a `detail_url`.
 *
 * Behaviour split:
 *   - INTERNAL routes (starting with `/`) — use react-router `<Link>` for
 *     same-tab SPA navigation. Preserves the customer's selections on
 *     browser-back (no re-fetch, no re-render).
 *   - EXTERNAL routes (http://, https://) — use `<a target="_blank">` with
 *     `rel="noopener noreferrer nofollow"`.
 */
function ViewDetailsLink({ line, testid }) {
  const url = (line.detail_url || "").trim();
  const location = useLocation();
  if (!url) return null;
  const isInternal = url.startsWith("/");
  const stop = (e) => e.stopPropagation();
  const className =
    "inline-flex items-center gap-1 text-xs font-semibold text-cyan-700 hover:text-cyan-900 hover:underline mt-1.5";

  if (isInternal) {
    return (
      <Link
        data-testid={testid}
        to={url}
        // Pass intent via Link state — document.referrer is unreliable
        // across react-router pushState navigation (see iter45 RCA).
        // `returnTo` lets the detail page navigate back without depending
        // on history depth.
        state={{ fromEstimate: true, returnTo: location.pathname + location.search }}
        onClick={stop}
        className={className}
      >
        View details <ExternalLink className="w-3 h-3" />
      </Link>
    );
  }
  return (
    <a
      data-testid={testid}
      href={url}
      target="_blank"
      rel="noopener noreferrer nofollow"
      onClick={stop}
      className={className}
    >
      View details <ExternalLink className="w-3 h-3" />
    </a>
  );
}

function Thumb({ url, name }) {
  if (!url) {
    return (
      <div data-testid="proposal-line-thumb-placeholder" className="w-20 h-20 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0">
        <ImageIcon className="w-6 h-6 text-slate-400" />
      </div>
    );
  }
  // Phase 2L-A49 — remote URLs (http/https) are absolute, data:image
  // blobs render inline, only prepend the backend host for our own
  // relative /api/uploads/... paths.
  const src = resolveImageUrl(url);
  return (
    <img
      data-testid="proposal-line-thumb"
      src={src}
      alt={name || ""}
      className="w-20 h-20 rounded-xl object-cover border border-slate-200 flex-shrink-0"
    />
  );
}

function LineCard({ line, selected, onToggle, locked, approved }) {
  const isRequired = line.line_status === "required";
  const tag = STATUS_TAG[line.line_status] || STATUS_TAG.optional;
  const desc = compactDescription(line);
  return (
    <div
      data-testid={`proposal-line-${line.product_id}`}
      className={`flex gap-4 p-4 sm:p-5 rounded-2xl border transition-all ${
        selected
          ? "bg-white border-cyan-200 shadow-[0_2px_12px_rgba(6,182,212,0.08)]"
          : "bg-slate-50/50 border-slate-200"
      }`}
    >
      <Thumb url={line.image_url} name={line.name} />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className={`inline-flex items-center text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full ${tag.bg} ${tag.text}`}>
                {tag.label}
              </span>
              {isRequired && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold tracking-wider text-slate-500">
                  <Lock className="w-3 h-3" /> Included / required
                </span>
              )}
            </div>
            <h3 className="font-display font-semibold text-slate-900 text-lg leading-tight">{line.name}</h3>
            {desc && (
              <p
                data-testid={`proposal-line-${line.product_id}-desc`}
                className="text-sm text-slate-600 mt-1.5 leading-snug line-clamp-3"
                title={desc}
              >
                {desc}
              </p>
            )}
            <ViewDetailsLink line={line} testid={`proposal-line-${line.product_id}-details`} />
            <div className="text-xs text-slate-500 mt-2">
              <span className="tabular-nums">×{line.qty}</span>
              <span className="mx-2 text-slate-300">·</span>
              <span className="tabular-nums">{money(line.unit_price_cents)}</span>
              <span className="ml-1 text-slate-400">{line.gst_included ? "inc GST" : "+ GST"}</span>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-base font-bold text-slate-900 tabular-nums">{money(line.line_total_cents)}</div>
            {!isRequired && !approved && (
              <button
                data-testid={`proposal-toggle-${line.product_id}`}
                disabled={locked}
                onClick={() => onToggle(line.product_id)}
                className={`mt-2 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${
                  selected
                    ? "bg-cyan-600 text-white hover:bg-cyan-700"
                    : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                {selected ? <><Check className="w-3.5 h-3.5" /> Selected</> : "Add to approval"}
              </button>
            )}
            {(isRequired || approved) && selected && (
              <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700">
                <Check className="w-3 h-3" /> Included
              </div>
            )}
            {approved && !selected && (
              <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-slate-400">
                <X className="w-3 h-3" /> Declined
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** OptionGroup — radio-style mutually-exclusive card group.
 *  Phase 2L-A25: highest-priced option (or admin's explicit default) is
 *  pre-selected and marked with the "Recommended" / "Best protection"
 *  badge from `group.recommended_badge_text`.
 */
function OptionGroupCard({ group, members, chosenProductId, onChoose, locked, approved }) {
  const groupId = group.id;
  const badgeText = (group.recommended_badge_text || "Recommended").trim();
  // Default option = the line flagged is_default_option (set server-side
  // at draft creation). Falls back to the highest-priced member.
  const defaultId =
    members.find((m) => m.is_default_option)?.product_id ??
    members.slice().sort((a, b) => (b.unit_price_cents || 0) - (a.unit_price_cents || 0))[0]?.product_id;

  return (
    <section data-testid={`proposal-option-group-${groupId}`} className="mb-8">
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700 mb-2 flex items-center gap-1.5">
        <Star className="w-3.5 h-3.5" /> {group.label || "Choose your system option"}
      </div>
      {group.description && (
        <p className="text-sm text-slate-600 mb-3 leading-relaxed">{group.description}</p>
      )}
      <div role="radiogroup" aria-labelledby={`group-${groupId}-label`} className="space-y-3">
        {members.map((line) => {
          const isChosen = chosenProductId === line.product_id;
          const isDefault = line.product_id === defaultId;
          return (
            <button
              key={line.product_id}
              type="button"
              role="radio"
              aria-checked={isChosen}
              disabled={locked || approved}
              data-testid={`proposal-option-${groupId}-${line.product_id}`}
              onClick={() => onChoose(groupId, line.product_id)}
              className={`w-full text-left flex gap-4 p-4 sm:p-5 rounded-2xl border transition-all ${
                isChosen
                  ? "bg-white border-cyan-500 ring-2 ring-cyan-200 shadow-[0_2px_12px_rgba(6,182,212,0.12)]"
                  : "bg-slate-50/50 border-slate-200 hover:border-slate-300"
              }`}
            >
              <Thumb url={line.image_url} name={line.name} />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {isDefault && (
                        <span
                          data-testid={`proposal-option-${line.product_id}-badge`}
                          className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200"
                        >
                          <Star className="w-3 h-3" /> {badgeText}
                        </span>
                      )}
                      {isChosen && !isDefault && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-cyan-600 text-white">
                          <Check className="w-3 h-3" /> Selected
                        </span>
                      )}
                    </div>
                    <h3 className="font-display font-semibold text-slate-900 text-lg leading-tight">{line.name}</h3>
                    {(() => {
                      const desc = compactDescription(line);
                      return desc ? (
                        <p
                          data-testid={`proposal-option-${line.product_id}-desc`}
                          className="text-sm text-slate-600 mt-1.5 leading-snug line-clamp-3"
                          title={desc}
                        >
                          {desc}
                        </p>
                      ) : null;
                    })()}
                    <ViewDetailsLink line={line} testid={`proposal-option-${line.product_id}-details`} />
                    <div className="text-xs text-slate-500 mt-2">
                      <span className="tabular-nums">×{line.qty}</span>
                      <span className="mx-2 text-slate-300">·</span>
                      <span className="tabular-nums">{money(line.unit_price_cents)}</span>
                      <span className="ml-1 text-slate-400">{line.gst_included ? "inc GST" : "+ GST"}</span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-base font-bold text-slate-900 tabular-nums">{money(line.line_total_cents)}</div>
                    <div className="mt-2">
                      <span
                        aria-hidden="true"
                        className={`inline-flex items-center justify-center w-6 h-6 rounded-full border-2 transition-all ${
                          isChosen ? "border-cyan-600 bg-cyan-600" : "border-slate-300 bg-white"
                        }`}
                      >
                        {isChosen && <Check className="w-3.5 h-3.5 text-white" />}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function ConfirmDialog({ open, proposal, selections, groupChoices, computedTotal, onCancel, onConfirm, submitting }) {
  if (!open) return null;
  const lines = proposal.lines || [];
  const declaredGroupIds = new Set((proposal.option_groups || []).map((g) => g.id));
  const isGroupMember = (l) => l.option_group_id && declaredGroupIds.has(l.option_group_id);
  const groupMemberIds = new Set(lines.filter(isGroupMember).map((l) => l.product_id));
  const chosenGroupProductIds = new Set(Object.values(groupChoices));

  const approved = lines.filter((l) => {
    if (l.line_status === "required") return true;
    if (isGroupMember(l)) return chosenGroupProductIds.has(l.product_id);
    return !!selections[l.product_id];
  });
  const declined = lines.filter((l) => {
    if (l.line_status === "required") return false;
    if (isGroupMember(l)) return groupMemberIds.has(l.product_id) && !chosenGroupProductIds.has(l.product_id);
    return !selections[l.product_id];
  });

  return (
    <div data-testid="proposal-confirm-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white max-w-lg w-full rounded-3xl shadow-2xl overflow-hidden">
        <div className="bg-slate-900 text-white p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-cyan-300">Confirm acceptance</div>
          <h2 className="font-display text-2xl font-bold mt-1">Accept selected items</h2>
          <p className="text-sm text-slate-300 mt-2">You are accepting the selected items below for an indicative total of <strong className="text-white tabular-nums">{money(computedTotal)}</strong>.</p>
        </div>
        <div className="p-6 max-h-[55vh] overflow-y-auto">
          <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 mb-2">✓ Items being accepted</div>
          <ul className="space-y-1.5 mb-5">
            {approved.map((l) => (
              <li key={`a-${l.product_id}`} className="flex items-start justify-between gap-3 text-sm">
                <span className="text-slate-800"><span className="text-slate-400 mr-1.5">×{l.qty}</span>{l.name}</span>
                <span className="tabular-nums font-medium text-slate-700">{money(l.line_total_cents)}</span>
              </li>
            ))}
            {approved.length === 0 && <li className="text-xs text-slate-400">None selected yet.</li>}
          </ul>
          {declined.length > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-wider font-bold text-rose-600 mb-2">✗ Not selected</div>
              <ul className="space-y-1.5 mb-5">
                {declined.map((l) => (
                  <li key={`d-${l.product_id}`} className="flex items-start justify-between gap-3 text-sm text-slate-500">
                    <span><span className="text-slate-400 mr-1.5">×{l.qty}</span>{l.name}</span>
                    <span className="tabular-nums">{money(l.line_total_cents)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
          <div className="border-t border-slate-200 pt-4 flex items-center justify-between">
            <div className="text-xs uppercase font-semibold tracking-wider text-slate-500">Accepted estimate total</div>
            <div data-testid="proposal-confirm-total" className="text-2xl font-bold tabular-nums text-slate-900">{money(computedTotal)}</div>
          </div>
          {/* Phase 2J-AI — onsite-inspection clarification block, locked
              wording per Stephen's spec. Shown in modal, public banner,
              email body, and PDF footer. */}
          <div data-testid="proposal-confirm-clarification" className="mt-4 bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-sm text-emerald-900 leading-relaxed">
            <p className="font-semibold mb-1">Estimate Accepted — subject to an onsite inspection by a PWS technician.</p>
            <p className="mt-1">A PWS team member will contact you shortly to schedule the onsite inspection and confirm final pricing.</p>
            <p className="mt-1">There is no obligation until the final proposal has been provided and approved by you.</p>
          </div>
        </div>
        <div className="bg-slate-50 px-6 py-4 flex items-center justify-end gap-2 border-t border-slate-200">
          <button onClick={onCancel} disabled={submitting} className="px-4 py-2 rounded-full text-sm font-semibold text-slate-700 hover:bg-slate-200 transition-colors">Cancel</button>
          <button
            data-testid="proposal-confirm-approve-btn"
            onClick={onConfirm}
            disabled={submitting}
            className="px-5 py-2 rounded-full text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 transition-colors"
          >
            {submitting ? "Accepting…" : "Accept Estimate"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PublicProposal() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [proposal, setProposal] = useState(null);
  // product_id -> bool, for non-group lines only (recommended + optional)
  const [selections, setSelections] = useState({});
  // group_id -> product_id, exactly one chosen per group at all times
  const [groupChoices, setGroupChoices] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [customerNotes, setCustomerNotes] = useState("");

  useEffect(() => {
    let cancelled = false;
    axios.get(apiPath(`/api/proposals/public/${token}`))
      .then(({ data }) => {
        if (cancelled) return;
        setProposal(data);
        // Initial selection state.
        // Required → always true (locked).
        // Recommended (non-group) → true by default (can untick).
        // Optional (non-group) → false by default.
        // Group members → handled in groupChoices, NOT in selections.
        // Already-approved proposal → honour the persisted `selected` flag.
        const approvedView = data.status === "approved";
        const init = {};
        const groupInit = {};
        for (const ln of data.lines || []) {
          if (ln.option_group_id) {
            // Group members: stash in groupChoices keyed by group id.
            if (approvedView) {
              if (ln.selected) groupInit[ln.option_group_id] = ln.product_id;
            } else if (ln.is_default_option) {
              groupInit[ln.option_group_id] = ln.product_id;
            }
            // selections map gets the group member set false initially —
            // the chosen product is sent as selected:true at approve time
            // from groupChoices, not from selections.
            continue;
          }
          if (ln.line_status === "required") init[ln.product_id] = true;
          else if (approvedView) init[ln.product_id] = !!ln.selected;
          else if (ln.line_status === "recommended") init[ln.product_id] = true;
          else init[ln.product_id] = false;
        }
        // Safety net: every option_group declared in proposal.option_groups
        // MUST have a default. If none of its members had is_default_option,
        // pick the highest-priced one.
        for (const g of data.option_groups || []) {
          if (groupInit[g.id]) continue;
          const members = (data.lines || []).filter((ln) => ln.option_group_id === g.id);
          if (members.length === 0) continue;
          const highest = members
            .slice()
            .sort((a, b) => (b.unit_price_cents || 0) - (a.unit_price_cents || 0))[0];
          groupInit[g.id] = highest.product_id;
        }

        // Phase 2L-A29 (iter46 fix): restore the customer's in-flight
        // selections AFTER defaults are computed. Used when the customer
        // navigated away to a /products/{slug} detail page and back —
        // navigate(-1) restores the URL but not React state, so we mirror
        // it through sessionStorage keyed by token. Skipped for already-
        // approved proposals (those are read-only and the snapshot rules).
        let restoredSelections = init;
        let restoredGroupChoices = groupInit;
        let restoredNotes = "";
        if (!approvedView) {
          try {
            const cached = sessionStorage.getItem(`pwsProposalState:${token}`);
            if (cached) {
              const parsed = JSON.parse(cached);
              if (parsed.selections && typeof parsed.selections === "object") {
                restoredSelections = { ...init, ...parsed.selections };
                // Never let cache flip a required line off — server forces it
                // anyway, but UI must stay consistent.
                for (const ln of data.lines || []) {
                  if (ln.line_status === "required") restoredSelections[ln.product_id] = true;
                }
              }
              if (parsed.groupChoices && typeof parsed.groupChoices === "object") {
                // Only honour cached group choices for groups that still
                // exist on the proposal AND for product_ids that are still
                // members of that group.
                const validatedGroups = { ...groupInit };
                for (const [gid, pid] of Object.entries(parsed.groupChoices)) {
                  const stillMember = (data.lines || []).some(
                    (ln) => ln.option_group_id === gid && ln.product_id === pid
                  );
                  if (stillMember) validatedGroups[gid] = pid;
                }
                restoredGroupChoices = validatedGroups;
              }
              if (typeof parsed.customerNotes === "string") {
                restoredNotes = parsed.customerNotes;
              }
            }
          } catch (_) {
            // Cache corrupt → silently fall back to defaults.
          }
        }
        setSelections(restoredSelections);
        setGroupChoices(restoredGroupChoices);
        if (restoredNotes) setCustomerNotes(restoredNotes);
      })
      .catch((e) => {
        const detail = e.response?.data?.detail || e.message;
        setError(detail);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  // Phase 2L-A29 (iter46 fix): mirror in-flight selections into
  // sessionStorage on every change so the navigate-away-and-back trip
  // (View details → /products/tr20uv → back) survives. Keyed by token so
  // multiple proposals don't bleed into each other. Only after the
  // initial proposal load so we don't wipe the cache with empty state.
  useEffect(() => {
    if (!proposal || proposal.status === "approved") return;
    try {
      sessionStorage.setItem(
        `pwsProposalState:${token}`,
        JSON.stringify({ selections, groupChoices, customerNotes }),
      );
    } catch (_) { /* quota / disabled — silently ignore */ }
  }, [token, proposal, selections, groupChoices, customerNotes]);

  const isApproved = proposal?.status === "approved";
  const isTerminal = !!proposal && ["approved", "declined", "cancelled", "expired"].includes(proposal.status);

  const toggleLine = (productId) => {
    setSelections((s) => ({ ...s, [productId]: !s[productId] }));
  };

  const chooseGroupOption = (groupId, productId) => {
    setGroupChoices((g) => ({ ...g, [groupId]: productId }));
  };

  // Live total — required items always count; for non-group lines, use
  // `selections`; for group members, only the chosen product per group
  // counts. Backend re-computes server-side at approval; this is purely
  // for the customer's preview.
  const liveTotal = useMemo(() => {
    if (!proposal) return 0;
    const chosenIds = new Set(Object.values(groupChoices));
    let total = 0;
    for (const ln of proposal.lines || []) {
      const required = ln.line_status === "required";
      let selected;
      if (required) selected = true;
      else if (ln.option_group_id) selected = chosenIds.has(ln.product_id);
      else selected = !!selections[ln.product_id];
      if (selected) total += ln.line_total_cents || 0;
    }
    return total;
  }, [proposal, selections, groupChoices]);

  const displayTotal = isApproved
    ? (proposal.approved_total_cents || 0)
    : liveTotal;

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      // Build the selections payload:
      //   - Skip required lines (server forces them on).
      //   - Non-group lines: send their selections[] value.
      //   - Group members: chosen → selected:true, siblings → selected:false.
      const chosenIds = new Set(Object.values(groupChoices));
      const payload = {
        selections: (proposal.lines || [])
          .filter((l) => l.line_status !== "required")
          .map((l) => {
            if (l.option_group_id) {
              return { product_id: l.product_id, selected: chosenIds.has(l.product_id) };
            }
            return { product_id: l.product_id, selected: !!selections[l.product_id] };
          }),
        customer_notes: customerNotes.trim() || null,
      };
      const { data } = await axios.post(apiPath(`/api/proposals/public/${token}/approve`), payload);
      setProposal(data);
      setConfirming(false);
      // Phase 2L-A29: clear the in-flight selections cache once approval
      // succeeds — from now on, the snapshot rules and any cached
      // pre-approval state would be misleading on re-visit.
      try { sessionStorage.removeItem(`pwsProposalState:${token}`); } catch (_) {}
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      setError(detail);
      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  };

  // ============ RENDER ============
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-slate-500 text-sm">Loading estimate…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div data-testid="proposal-error" className="max-w-md w-full bg-white rounded-2xl border border-rose-200 p-8 text-center">
          <AlertCircle className="w-10 h-10 text-rose-500 mx-auto mb-3" />
          <h1 className="font-display text-xl font-bold text-slate-900 mb-2">Estimate unavailable</h1>
          <p className="text-sm text-slate-600">{String(error)}</p>
        </div>
      </div>
    );
  }
  if (!proposal) return null;

  const brand = (proposal.brand || "pristine_water_systems").replace(/_/g, " ");
  const allLines = proposal.lines || [];
  const optionGroups = proposal.option_groups || [];
  const required = allLines.filter((l) => l.line_status === "required");
  // Lines only count as group members when their group is actually declared —
  // orphaned option_group_id references must never hide a line (prod bug Jun 2026).
  const declaredGroupIds = new Set(optionGroups.map((g) => g.id));
  const groupMemberIds = new Set(allLines
    .filter((l) => l.option_group_id && declaredGroupIds.has(l.option_group_id))
    .map((l) => l.product_id));
  // Non-group, non-required:
  const recommended = allLines.filter((l) => l.line_status === "recommended" && !groupMemberIds.has(l.product_id));
  const optional = allLines.filter((l) => l.line_status === "optional" && !groupMemberIds.has(l.product_id));

  // For each declared option_group, gather its members (preserve list order).
  const groupedMembers = optionGroups.map((g) => ({
    group: g,
    members: allLines.filter((l) => l.option_group_id === g.id),
  })).filter((g) => g.members.length > 0);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* HERO */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-900 to-cyan-900 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-cyan-300 mb-3">
            {brand.replace(/\b\w/g, (c) => c.toUpperCase())}
            {proposal.test_id && <span className="text-slate-500 ml-3">· Aware test {proposal.test_id}</span>}
          </div>
          <h1 data-testid="proposal-headline" className="font-display text-3xl sm:text-5xl font-bold leading-tight">
            {isApproved ? "Estimate Accepted" : "Your estimate"}
          </h1>
          <div className="mt-4 text-sm text-slate-300">
            Reference <span className="font-mono text-white">{proposal.reference}</span>
          </div>
          {(proposal.customer_name || proposal.property_address) && (
            <div className="mt-6 flex flex-wrap gap-x-8 gap-y-2 text-sm">
              {proposal.customer_name && (
                <div><div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Prepared for</div><div className="text-white font-semibold mt-0.5">{proposal.customer_name}</div></div>
              )}
              {proposal.property_address && (
                <div><div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Property</div><div className="text-white mt-0.5">{proposal.property_address}</div></div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* BODY */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {isApproved && (
          <div data-testid="proposal-success-banner" className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mb-8 flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
              <Check className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-display text-xl font-bold text-emerald-900">Estimate Accepted — subject to onsite inspection</h2>
              <p className="text-sm text-emerald-800 mt-2 leading-relaxed">
                A PWS team member will contact you shortly to schedule the onsite inspection and confirm final pricing.
              </p>
              <p className="text-sm text-emerald-800 mt-2 leading-relaxed">
                There is no obligation until the final proposal has been provided and approved by you.
              </p>
              <div className="mt-3 text-xs text-emerald-700">
                Accepted on <strong>{proposal.approved_at ? formatDateTimeAU(proposal.approved_at) : "just now"}</strong>
              </div>
            </div>
          </div>
        )}

        {/* Required items first */}
        {required.length > 0 && (
          <section data-testid="proposal-required-section" className="mb-8">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">Included / required</div>
            <div className="space-y-3">
              {required.map((l) => (
                <LineCard key={l.product_id} line={l} selected={true} locked={isTerminal} approved={isApproved} onToggle={() => {}} />
              ))}
            </div>
          </section>
        )}

        {/* Option groups (Phase 2L-A25) */}
        {groupedMembers.map(({ group, members }) => (
          <OptionGroupCard
            key={group.id}
            group={group}
            members={members}
            chosenProductId={
              isApproved
                ? members.find((m) => m.selected)?.product_id
                : groupChoices[group.id]
            }
            onChoose={chooseGroupOption}
            locked={isTerminal}
            approved={isApproved}
          />
        ))}

        {/* Recommended add-ons (non-group) */}
        {recommended.length > 0 && (
          <section data-testid="proposal-recommended-section" className="mb-8">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700 mb-3 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Recommended add-ons
            </div>
            <div className="space-y-3">
              {recommended.map((l) => (
                <LineCard
                  key={l.product_id}
                  line={l}
                  selected={isApproved ? !!l.selected : !!selections[l.product_id]}
                  locked={isTerminal}
                  approved={isApproved}
                  onToggle={toggleLine}
                />
              ))}
            </div>
          </section>
        )}

        {/* Optional items (non-group) */}
        {optional.length > 0 && (
          <section data-testid="proposal-optional-section" className="mb-8">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">Optional items</div>
            <div className="space-y-3">
              {optional.map((l) => (
                <LineCard
                  key={l.product_id}
                  line={l}
                  selected={isApproved ? !!l.selected : !!selections[l.product_id]}
                  locked={isTerminal}
                  approved={isApproved}
                  onToggle={toggleLine}
                />
              ))}
            </div>
          </section>
        )}

        {/* Customer notes (only while approvable) */}
        {!isTerminal && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-6">
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Notes for the team (optional)</label>
            <textarea
              data-testid="proposal-customer-notes"
              value={customerNotes}
              onChange={(e) => setCustomerNotes(e.target.value)}
              rows={3}
              placeholder="Site access, preferred times, questions…"
              className="mt-2 w-full text-sm bg-slate-50 border border-slate-200 rounded-lg p-3 focus:outline-none focus:border-cyan-400 focus:bg-white transition-colors resize-none"
            />
          </div>
        )}

        {/* Sticky total + accept */}
        {!isApproved && (
          <div className="sticky bottom-4 z-20">
            <div className="bg-slate-900 text-white rounded-2xl shadow-2xl p-5 flex items-center justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-cyan-300">Indicative total</div>
                <div data-testid="proposal-live-total" className="text-2xl sm:text-3xl font-bold tabular-nums">{money(displayTotal)}</div>
              </div>
              <button
                data-testid="proposal-approve-btn"
                disabled={isTerminal}
                onClick={() => setConfirming(true)}
                className="px-5 sm:px-7 py-3 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-slate-900 font-bold rounded-full text-sm sm:text-base whitespace-nowrap transition-colors"
              >
                Accept Estimate →
              </button>
            </div>
          </div>
        )}

        {/* Accepted-state final card */}
        {isApproved && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Accepted estimate total — subject to onsite inspection</div>
            <div data-testid="proposal-approved-total" className="text-3xl font-bold text-slate-900 tabular-nums mt-1">{money(displayTotal)}</div>
            <p className="text-sm text-slate-600 mt-4 leading-relaxed">
              A PWS team member will contact you shortly to schedule the onsite inspection and confirm final pricing. There is no obligation until the final proposal has been provided and approved by you.
            </p>
            <div className="mt-4 text-xs text-slate-500">
              Questions? Call <strong>(07) 5327 3473</strong> or reply to your confirmation email.
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirming}
        proposal={proposal}
        selections={selections}
        groupChoices={groupChoices}
        computedTotal={liveTotal}
        onCancel={() => setConfirming(false)}
        onConfirm={handleApprove}
        submitting={submitting}
      />
    </div>
  );
}
